import { useState } from 'react';
import Seo from '@/components/base/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import { siteName, categories, games, type GameItem } from '@/mocks/siteData';

function GameModal({ game, onClose }: { game: GameItem; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-background-50 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{game.title}</h2>
          <button type="button" aria-label="关闭" onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <p className="mt-2 text-sm text-accent-600">{game.tagline}</p>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="aspect-[3/4] overflow-hidden rounded-md">
            <img src={game.cover} alt={`${game.title}游戏封面`} className="h-full w-full object-cover object-top" />
          </div>
          <div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md bg-background-100 p-3">
                <span className="block font-label text-xs text-foreground-500">类型</span>
                <span className="mt-1 block text-sm font-bold text-foreground-950">{game.category}</span>
              </div>
              <div className="rounded-md bg-background-100 p-3">
                <span className="block font-label text-xs text-foreground-500">时长</span>
                <span className="mt-1 block text-sm font-bold text-foreground-950">{game.playtime}</span>
              </div>
              <div className="rounded-md bg-background-100 p-3">
                <span className="block font-label text-xs text-foreground-500">路线数</span>
                <span className="mt-1 block text-sm font-bold text-foreground-950">{game.routeCount} 条</span>
              </div>
              <div className="rounded-md bg-background-100 p-3">
                <span className="block font-label text-xs text-foreground-500">结局数</span>
                <span className="mt-1 block text-sm font-bold text-foreground-950">{game.endingCount} 个</span>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">{game.desc}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {game.tags.map((t) => (
                <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-800">{t}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-md border border-primary-200 bg-primary-50 p-4">
          <p className="flex items-center gap-2 font-label text-xs font-bold text-primary-700">
            <i className="ri-key-2-line"></i>隐藏剧情线索
          </p>
          <p className="mt-2 text-sm leading-relaxed text-primary-900">{game.hidden}</p>
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-background-200 pt-4">
          <span className="font-label text-xs text-foreground-500">推荐指数</span>
          <span className="font-label text-sm font-bold text-accent-600">
            <i className="ri-star-fill mr-1"></i>{game.rating.toFixed(1)} / 10
          </span>
        </div>
      </div>
    </div>
  );
}

export default function GamesPage() {
  const [active, setActive] = useState(categories[0]);
  const [selected, setSelected] = useState<GameItem | null>(null);
  const list = active === '全部' ? games : games.filter((g) => g.category === active);

  const jsonLd: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '火影同人游戏库',
      description: '收录剧情向火影同人RPG游戏及攻略。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: list.map((g, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: g.title,
        description: g.desc,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`火影同人游戏库 - 剧情向RPG攻略合集 | ${siteName}`}
        description="按剧情向、角色扮演、多结局、高自由分类浏览火影同人RPG游戏，查看每部作品的玩法时长、路线与结局数量，快速定位你的下一部作品。"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-28 md:px-6 md:pt-32">
        <nav aria-label="面包屑" className="mb-8 flex flex-wrap items-center gap-1 font-label text-xs text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">游戏库</span>
        </nav>

        <div className="text-center">
          <p className="font-label text-sm font-bold tracking-widest text-accent-600">游戏库</p>
          <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">剧情向火影同人RPG合集</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
            按类型筛选，查看每部作品的玩法路线与结局数量，快速定位适合你的下一部剧情作品。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                active === c ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((g) => (
            <article key={g.id} className="group overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="relative aspect-[3/4] overflow-hidden">
                <img src={g.cover} alt={`${g.title}游戏封面`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-3 top-3 rounded-md bg-foreground-950/70 px-2 py-1 font-label text-xs text-background-50 backdrop-blur">{g.category}</span>
              </div>
              <div className="p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">{g.title}</h2>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">{g.tagline}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <span className="font-label text-foreground-500">{g.routeCount} 路线</span>
                    <span className="font-label text-foreground-500">{g.endingCount} 结局</span>
                  </div>
                  <span className="font-label text-sm font-bold text-accent-600">
                    <i className="ri-star-fill mr-1"></i>{g.rating.toFixed(1)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(g)}
                  className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看攻略
                </button>
              </div>
            </article>
          ))}
        </div>
      </main>
      {selected && <GameModal game={selected} onClose={() => setSelected(null)} />}
      <Footer />
      <ScrollTopButton />
    </>
  );
}